import type { PollAnswerInput, PollDetailDto, PollQuestionDto } from '@ukinfo/shared';
import { useEffect, useState } from 'react';
import { Button, Textarea, cn } from '../ui';

type Draft = Record<string, { optionIds: string[]; text: string }>;

function initialDraft(poll: PollDetailDto): Draft {
  return Object.fromEntries(
    poll.questions.map((question) => {
      const mine = poll.myAnswers?.[question.id];
      return [question.id, { optionIds: mine?.optionIds ?? [], text: mine?.text ?? '' }];
    }),
  );
}

function isAnswered(question: PollQuestionDto, draft: Draft): boolean {
  const entry = draft[question.id];
  return question.kind === 'TEXT' ? Boolean(entry?.text.trim()) : (entry?.optionIds.length ?? 0) > 0;
}

/**
 * Antwortformular einer Umfrage.
 *
 * Gemeinsam fuer angemeldete Personen und den persoenlichen Link: beide
 * reichen nur ein, was die Person selbst ausgewaehlt hat.
 */
export function PollAnswerForm({
  poll,
  onSubmit,
  submitting,
  onCancel,
}: {
  poll: PollDetailDto;
  onSubmit: (input: PollAnswerInput) => void;
  submitting: boolean;
  onCancel?: () => void;
}): JSX.Element {
  const [draft, setDraft] = useState<Draft>(() => initialDraft(poll));
  useEffect(() => setDraft(initialDraft(poll)), [poll]);

  const missing = poll.questions.filter((q) => q.required && !isAnswered(q, draft));
  const nothing = poll.questions.every((q) => !isAnswered(q, draft));

  const set = (questionId: string, patch: Partial<Draft[string]>): void =>
    setDraft((current) => ({ ...current, [questionId]: { ...current[questionId]!, ...patch } }));

  const toggle = (question: PollQuestionDto, optionId: string): void => {
    const current = draft[question.id]?.optionIds ?? [];
    if (question.kind === 'SINGLE') set(question.id, { optionIds: [optionId] });
    else set(question.id, { optionIds: current.includes(optionId) ? current.filter((id) => id !== optionId) : [...current, optionId] });
  };

  return (
    <form
      className="space-y-5"
      onSubmit={(event) => {
        event.preventDefault();
        onSubmit({
          answers: poll.questions.map((question) => ({
            questionId: question.id,
            optionIds: question.kind === 'TEXT' ? [] : draft[question.id]?.optionIds ?? [],
            text: question.kind === 'TEXT' ? draft[question.id]?.text.trim() || null : null,
          })),
        });
      }}
    >
      {poll.questions.map((question, index) => (
        <fieldset key={question.id} className="space-y-2">
          <legend className="text-sm font-medium text-slate-800">
            {poll.questions.length > 1 && <span className="text-slate-400">{index + 1}. </span>}
            {question.text}
            {!question.required && <span className="font-normal text-slate-400"> (freiwillig)</span>}
          </legend>
          {question.kind === 'TEXT' ? (
            <Textarea
              aria-label={question.text}
              rows={3}
              maxLength={2000}
              value={draft[question.id]?.text ?? ''}
              onChange={(event) => set(question.id, { text: event.target.value })}
            />
          ) : (
            <div className="grid gap-2 sm:grid-cols-2">
              {question.options.map((option) => {
                const selected = draft[question.id]?.optionIds.includes(option.id) ?? false;
                return (
                  <label
                    key={option.id}
                    className={cn(
                      'flex cursor-pointer items-center gap-2 rounded-lg border px-3 py-2.5 text-sm transition-colors',
                      selected ? 'border-brand-500 bg-brand-50 text-brand-900' : 'border-slate-200 hover:border-slate-300',
                    )}
                  >
                    <input
                      type={question.kind === 'SINGLE' ? 'radio' : 'checkbox'}
                      name={question.id}
                      checked={selected}
                      onChange={() => toggle(question, option.id)}
                      className="h-4 w-4 accent-brand-600"
                    />
                    {option.label}
                  </label>
                );
              })}
            </div>
          )}
          {question.kind === 'MULTIPLE' && <p className="hint">Mehrere Antworten möglich</p>}
        </fieldset>
      ))}

      {poll.anonymous && (
        <p className="rounded-lg bg-slate-50 px-3 py-2 text-xs text-slate-600">
          Anonym: Deine Antwort wird ohne deinen Namen gespeichert. Sichtbar ist nur, dass du geantwortet hast.
          Nach dem Absenden lässt sie sich deshalb nicht mehr ändern.
        </p>
      )}

      <div className="flex flex-wrap items-center gap-2">
        <Button type="submit" loading={submitting} disabled={missing.length > 0 || nothing}>
          {poll.myAnswers ? 'Antwort ändern' : 'Antwort senden'}
        </Button>
        {onCancel && <Button type="button" variant="secondary" onClick={onCancel}>Abbrechen</Button>}
        {missing.length > 0 && (
          <span className="text-xs text-slate-500">Noch offen: {missing.map((q) => `„${q.text}“`).join(', ')}</span>
        )}
      </div>
    </form>
  );
}

/** Ergebnis als Balken. Freitexte nur, wenn der Server sie mitliefert (Auswertung). */
export function PollResults({ poll }: { poll: PollDetailDto }): JSX.Element | null {
  if (!poll.results) return null;
  return (
    <div className="space-y-5">
      {poll.questions.map((question) => {
        const result = poll.results?.find((r) => r.questionId === question.id);
        if (!result) return null;
        const max = Math.max(1, ...Object.values(result.counts));
        return (
          <div key={question.id} className="space-y-2">
            <p className="text-sm font-medium text-slate-800">
              {question.text}
              <span className="ml-2 text-xs font-normal text-slate-500">{result.answered} Antworten</span>
            </p>
            {question.kind === 'TEXT' ? (
              result.texts === undefined ? (
                <p className="hint">Freitexte sieht nur, wer die Umfrage auswertet.</p>
              ) : result.texts.length === 0 ? (
                <p className="hint">Noch keine Antworten.</p>
              ) : (
                <ul className="space-y-1.5">
                  {result.texts.map((entry, index) => (
                    <li key={index} className="rounded-lg bg-slate-50 px-3 py-2 text-sm text-slate-700">
                      <span className="whitespace-pre-wrap break-words">{entry.text}</span>
                      {entry.personName && <span className="mt-0.5 block text-xs text-slate-500">{entry.personName}</span>}
                    </li>
                  ))}
                </ul>
              )
            ) : (
              <ul className="space-y-1.5">
                {question.options.map((option) => {
                  const count = result.counts[option.id] ?? 0;
                  const share = result.answered > 0 ? Math.round((count / result.answered) * 100) : 0;
                  return (
                    <li key={option.id} className="text-sm">
                      <div className="flex justify-between gap-3">
                        <span className="text-slate-700">{option.label}</span>
                        <span className="tabular-nums text-slate-500">{count} · {share} %</span>
                      </div>
                      <div className="mt-1 h-2 overflow-hidden rounded-full bg-slate-100" aria-hidden="true">
                        <div className="h-full rounded-full bg-brand-500" style={{ width: `${(count / max) * 100}%` }} />
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        );
      })}
    </div>
  );
}
